// src/components/auth/AuthCodeHandler.js - Updated with new color scheme
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../utils/color';

const AuthCodeHandler = () => {
  const [searchParams] = useSearchParams();
  const { authenticateWithMagicLink } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleAuthCode = async () => {
      // Backend sends: /auth/code?token={token}&email={email}
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token) {
        setError('Invalid authentication link - missing token');
        setLoading(false);
        return;
      }

      if (!email) {
        setError('Invalid authentication link - missing email');
        setLoading(false);
        return;
      }

      try {
        // Use the token to authenticate (backend expects token in URL path)
        await authenticateWithMagicLink(token);
        
        // Redirect to dubbing page after successful auth
        navigate('/dubbing', { replace: true });
      } catch (err) {        
        // Handle specific error types
        if (err.message.includes('expired')) {
          setError('This magic link has expired. Please request a new one.');
        } else if (err.message.includes('used')) {
          setError('This magic link has already been used. Please request a new one.');
        } else if (err.message.includes('Invalid')) {
          setError('Invalid magic link. Please request a new one.');
        } else {
          setError(err.message || 'Authentication failed');
        }
      } finally {
        setLoading(false);
      }
    };

    // Add delay to prevent double processing
    const timeoutId = setTimeout(handleAuthCode, 100);
    
    return () => clearTimeout(timeoutId);
  }, [searchParams, authenticateWithMagicLink, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: COLORS.gradients.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: COLORS.primary.main, mb: 2 }} />
            <Typography variant="h6" sx={{ color: COLORS.text.primary, mt: 2 }}>
              Authenticating...
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.text.secondary, mt: 1 }}>
              Please wait while we sign you in...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: COLORS.gradients.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <Alert
              severity="error"
              sx={{
                backgroundColor: `${COLORS.status.error}20`,
                color: COLORS.status.error,
                '& .MuiAlert-icon': { color: COLORS.status.error },
                mb: 2
              }}
            >
              {error}
            </Alert>
            <Typography variant="body1" sx={{ color: COLORS.text.secondary, mb: 2 }}>
              Please try requesting a new magic link.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: COLORS.primary.main, 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => navigate('/signin')}
            >
              Go back to sign in
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return null;
};

export default AuthCodeHandler;